// Soft paywall modal/sheet — Free plan limit chrome, EN/IT i18n, privacy wedge.
// Triggered by SoftPaywallContext on HTTP 402 SOFT_PAYWALL. Never locks account.
import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useSoftPaywall } from '../../contexts/SoftPaywallContext';
import { trackCheckoutStarted } from '../../services/conversionEvents';
import { analyticsReasonFromFeature } from '../../utils/softPaywall';
import api from '../../services/api';
import { SITE_ORIGIN } from '../../config/site';

const PLAN_COLORS = {
  standard: '#3b82f6',
  premium: '#8b5cf6',
};

const REASON_COPY_KEYS = {
  ocr_limit: 'ocr',
  splitsmart_limit: 'splitsmart',
  ai_premium: 'ai',
};

const SoftPaywallModal = () => {
  const { isOpen, close, payload } = useSoftPaywall();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [busy, setBusy] = useState(false);

  const feature = payload?.feature || payload?.paywall?.feature;
  const reason =
    payload?.analyticsReason ||
    analyticsReasonFromFeature(feature) ||
    'ocr_limit';
  const copyKey = REASON_COPY_KEYS[reason] || 'ocr';
  const recommendedPlan =
    payload?.paywall?.recommendedPlan ||
    (reason === 'ai_premium' ? 'premium' : 'standard');
  const priceEur =
    payload?.paywall?.priceEur ??
    (recommendedPlan === 'premium' ? 4.99 : 2.99);
  const trialDays = payload?.paywall?.trialDays ?? 60;
  const accent = PLAN_COLORS[recommendedPlan] || PLAN_COLORS.standard;

  const title = t(
    `subscription.softPaywall.${copyKey}.title`,
    reason === 'ai_premium'
      ? 'AI coach is on Premium'
      : reason === 'splitsmart_limit'
        ? 'Free covers 2 active splits'
        : "You've used this month's free receipt scans"
  );
  const body = t(
    `subscription.softPaywall.${copyKey}.body`,
    reason === 'ai_premium'
      ? 'Personalized tips on your data. Premium €4.99/mo — still no bank login, still no data selling.'
      : reason === 'splitsmart_limit'
        ? 'Need another SplitSmart group? Standard unlocks more shared expenses — privacy-first, no open banking.'
        : 'Free includes 3 OCR scans/month. Upgrade to Standard for more — still no bank login.'
  );
  const primaryCta = t(
    `subscription.softPaywall.${copyKey}.primaryCta`,
    reason === 'ai_premium'
      ? 'Get AI coach — €4.99/mo'
      : reason === 'splitsmart_limit'
        ? 'Unlock more splits — €2.99/mo'
        : 'Continue with Standard — €2.99/mo'
  );
  const altCta = t(
    `subscription.softPaywall.${copyKey}.altCta`,
    reason === 'ai_premium' ? 'Compare plans' : 'Start 60-day trial'
  );

  const finePrint = t(
    'subscription.softPaywall.finePrint',
    'No bank login required · Cancel anytime · GDPR'
  );
  const badge = t('subscription.softPaywall.badge', 'Free plan limit');
  const stayFree = t('subscription.softPaywall.stayOnFree', 'Stay on Free');

  const usedLimitHint = useMemo(() => {
    if (payload?.used == null || payload?.limit == null) return null;
    return t('subscription.softPaywall.usageHint', {
      used: payload.used,
      limit: payload.limit,
      defaultValue: '{{used}} / {{limit}} used',
    });
  }, [payload?.used, payload?.limit, t]);

  const startCheckout = async ({ trial = false } = {}) => {
    setBusy(true);
    try {
      await trackCheckoutStarted(feature || 'client', recommendedPlan, {
        reason,
        trial,
        priceEur,
        trialDays,
        source: 'soft_paywall',
      });

      const res = await api.post('/subscription/create-checkout-session', {
        planId: recommendedPlan,
        billingCycle: 'monthly',
        trial: trial || undefined,
        successUrl: `${SITE_ORIGIN}/subscription/success`,
        cancelUrl: `${SITE_ORIGIN}/subscription/plans`,
      });
      const sessionUrl =
        res.data?.data?.sessionUrl ||
        res.data?.sessionUrl ||
        res.data?.data?.url;
      if (sessionUrl) {
        window.location.href = sessionUrl;
        return;
      }
      // Fallback: in-app checkout / plans
      close();
      navigate(`/subscription/checkout/${recommendedPlan}`);
    } catch (err) {
      console.warn('Soft paywall checkout failed, opening plans', err);
      close();
      navigate('/subscription/plans');
    } finally {
      setBusy(false);
    }
  };

  const handlePrimary = () => {
    if (reason === 'ai_premium') {
      startCheckout({ trial: false });
    } else {
      startCheckout({ trial: false });
    }
  };

  const handleAlt = () => {
    if (reason === 'ai_premium') {
      trackCheckoutStarted(feature || 'client', recommendedPlan, {
        reason,
        action: 'compare_plans',
        source: 'soft_paywall',
      });
      close();
      navigate('/subscription/plans');
      return;
    }
    startCheckout({ trial: true });
  };

  const handleStayFree = () => {
    close();
  };

  return (
    <Dialog
      open={!!isOpen}
      onClose={handleStayFree}
      fullScreen={isMobile}
      fullWidth
      maxWidth="sm"
      aria-labelledby="soft-paywall-title"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: 'hidden',
          ...(isMobile
            ? {
                mt: 'auto',
                maxHeight: '92vh',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              }
            : {}),
        },
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${accent} 0%, ${theme.palette.primary.main} 100%)`,
          color: '#fff',
          px: 3,
          py: 2.5,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={handleStayFree}
          aria-label="close"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: '#fff',
            '&:hover': { background: 'rgba(255,255,255,0.15)' },
          }}
        >
          <CloseIcon />
        </IconButton>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockOutlinedIcon sx={{ color: '#fff' }} />
          </Box>
          <Box>
            <Chip
              size="small"
              label={badge}
              sx={{
                mb: 0.75,
                height: 22,
                fontWeight: 700,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: '#fff',
              }}
            />
            <Typography
              id="soft-paywall-title"
              variant="h6"
              sx={{ fontWeight: 800, lineHeight: 1.25, pr: 4 }}
            >
              {title}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
          {body}
        </Typography>
        {usedLimitHint && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {usedLimitHint}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: accent, mb: 2 }}
        >
          {recommendedPlan === 'premium'
            ? t('subscription.softPaywall.pricePremium', 'Premium · €{{price}}/mo', {
                price: priceEur ?? 4.99,
              })
            : t('subscription.softPaywall.priceStandard', 'Standard · €{{price}}/mo · {{days}}-day trial', {
                price: priceEur ?? 2.99,
                days: trialDays,
              })}
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', letterSpacing: 0.2 }}
        >
          {finePrint}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 0,
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1,
        }}
      >
        <Button
          variant="contained"
          size="large"
          disabled={busy}
          onClick={handlePrimary}
          sx={{
            borderRadius: 2,
            fontWeight: 800,
            textTransform: 'none',
            background: accent,
            '&:hover': { background: accent, filter: 'brightness(0.95)' },
          }}
        >
          {primaryCta}
        </Button>
        <Button
          variant="outlined"
          size="large"
          disabled={busy}
          onClick={handleAlt}
          sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
        >
          {altCta}
        </Button>
        <Button
          variant="text"
          size="medium"
          disabled={busy}
          onClick={handleStayFree}
          sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 600 }}
        >
          {stayFree}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SoftPaywallModal;
